import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

import { finalize, map, of, switchMap } from 'rxjs';

import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { ExportService } from '../../core/services/export.service';
import { AuthService } from '../auth/auth.service';
import {
  InventoryItem,
  StockIssue,
  StockLocationSuggestion,
} from '../../core/models/inventory.models';
import { isBranchFilterLocked } from '../../core/utils/branch-scope.util';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
  stockLevelClass,
} from '../../core/utils/format.util';
import { environment } from '../../environments/environment';
import {
  assertCanTake,
  parseNonNegativeNumber,
  parsePositiveInt,
} from '../../core/utils/stock-qty.util';

interface IssueLineItem {
  itemId: string;
  code: string;
  name: string;
  category: string;
  available: number;
  quantity: number;
  reserved: boolean;
  price: number;
  unavailableReason?: string;
  icon?: string;
}

interface PickerItemView {
  id: string;
  code: string;
  name: string;
  category: string;
  icon?: string;
  available: number;
  unavailableReason: string;
  canIssue: boolean;
  suggestions: StockLocationSuggestion[];
  suggestionText: string;
  price: number;
}

@Component({
  selector: 'app-issue-stock',
  imports: [FormsModule, CommonModule, RouterLink],
  standalone: true,
  templateUrl: './issue-stock.component.html',
  styleUrl: './issue-stock.component.css',
})
export class IssueStockComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly auth = inject(AuthService);
  private readonly exporter = inject(ExportService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly formatDate = formatDate;
  readonly stockLevelClass = stockLevelClass;
  readonly issuedOutStock = this.inventory.issuedOutStock;
  readonly issueStockList = this.inventory.issueStockList;
  readonly reservedIssueHolds = this.inventory.reservedIssueHolds;
  readonly branches = this.settings.scopedBranches;
  readonly branchSelectionLocked = computed(() =>
    isBranchFilterLocked(this.auth),
  );

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (this.branchId() || !branches.length) {
        return;
      }
      if (this.auth.currentBranchId() > 0) {
        this.branchId.set(String(this.auth.currentBranchId()));
      } else if (branches.length === 1) {
        this.branchId.set(branches[0].id);
      }
    });
  }

  ngOnInit(): void {
    this.issuedBy.set(this.auth.currentUserName());
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.refreshFromApi();
    }
  }

  readonly issuedToCase = signal('');
  readonly recipientPhone = signal('');
  readonly recipientLocation = signal('');
  readonly issueDate = signal(new Date().toISOString().slice(0, 10));
  readonly issuedBy = signal('');
  readonly branchId = signal('');
  readonly locationId = signal('');
  readonly notes = signal('');

  readonly itemSearch = signal('');
  readonly selectedItemId = signal<string | null>(null);
  readonly lineItems = signal<IssueLineItem[]>([]);

  readonly showItemModal = signal(false);
  readonly showConfirmModal = signal(false);
  readonly showEditIssueModal = signal(false);
  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');
  readonly lastIssuedTo = signal('');
  readonly editingIssueId = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly saving = signal(false);

  readonly locationsForBranch = computed(() =>
    this.settings.getLocations(this.branchId()),
  );

  readonly isEditingIssue = computed(() => this.editingIssueId() !== null);

  readonly editIssue = signal({
    issuedTo: '',
    branch: '',
    issueDate: '',
    items: 1,
    status: 'pending' as StockIssue['status'],
  });

  readonly filteredPickerItems = computed(() => {
    const search = this.itemSearch().trim().toLowerCase();
    const selected = new Set(this.lineItems().map((row) => row.itemId));
    const branchId = this.branchId();
    const locationId = this.locationId();

    return (
      this.inventory
        .allItems()
        // Issue Stock is consumables only. Rentables go out through Issue Equipment
        // so they come back via Check-In.
        .filter((item) => item.status === 'active' && !item.isRentable)
        .map((item) => this.toPickerItem(item, branchId, locationId))
        .filter((item) => {
          if (selected.has(item.id)) {
            return false;
          }
          if (!search) {
            return true;
          }
          return (
            item.code.toLowerCase().includes(search) ||
            item.name.toLowerCase().includes(search) ||
            item.category.toLowerCase().includes(search) ||
            item.unavailableReason.toLowerCase().includes(search) ||
            item.suggestionText.toLowerCase().includes(search)
          );
        })
    );
  });

  private toPickerItem(
    item: InventoryItem,
    branchId: string,
    locationId: string,
  ): PickerItemView {
    const locationName = this.locationsForBranch().find(
      (row) => row.id === locationId,
    )?.name;
    const suggestions = this.inventory.suggestStockLocations(item.id, {
      branchId,
      locationId,
    });
    const suggestionText = this.inventory.formatStockSuggestions(suggestions);

    let unavailableReason = '';
    let available = 0;

    if (item.status === 'inactive' || item.status === 'discontinued') {
      unavailableReason = `Item is ${item.status}`;
    } else if (!branchId || !locationId) {
      unavailableReason = 'Select a branch and store location first';
    } else {
      available = this.inventory.availableQuantityAtLocation(
        item.id,
        branchId,
        locationId,
        locationName,
      );
      if (available <= 0) {
        unavailableReason = suggestionText
          ? `Not available here. Available at ${suggestionText}`
          : 'No available quantity at the selected store location';
      }
    }

    return {
      id: item.id,
      code: item.code,
      name: item.name,
      category: item.category,
      icon: item.icon,
      available,
      unavailableReason,
      canIssue: !unavailableReason && available > 0,
      suggestions,
      suggestionText,
      price: item.price,
    };
  }

  readonly totalItems = computed(() => this.lineItems().length);

  readonly totalQuantity = computed(() =>
    this.lineItems().reduce((sum, row) => sum + row.quantity, 0),
  );

  readonly reservedCount = computed(
    () => this.lineItems().filter((row) => row.reserved).length,
  );

  readonly consumableIssuedCount = computed(
    () => this.lineItems().filter((row) => !row.reserved).length,
  );

  readonly estimatedValue = computed(() =>
    this.lineItems().reduce((sum, row) => sum + row.quantity * row.price, 0),
  );

  readonly selectedBranchName = computed(
    () => this.branches().find((row) => row.id === this.branchId())?.name ?? '',
  );

  readonly selectedLocationName = computed(
    () =>
      this.locationsForBranch().find((row) => row.id === this.locationId())
        ?.name ?? '',
  );

  readonly isFormValid = computed(
    () =>
      this.issuedToCase().trim().length > 0 &&
      this.recipientPhone().trim().length > 0 &&
      this.recipientLocation().trim().length > 0 &&
      this.branchId().trim().length > 0 &&
      this.locationId().trim().length > 0 &&
      this.lineItems().length > 0 &&
      this.issueDate().length > 0,
  );

  onBranchChange(branchId: string): void {
    this.branchId.set(branchId);
    this.locationId.set('');
    this.lineItems.set([]);
  }

  onLocationChange(locationId: string): void {
    this.locationId.set(locationId);
    this.lineItems.set([]);
  }

  openItemModal(): void {
    if (!this.branchId() || !this.locationId()) {
      this.showToastMessage(
        'Select a branch and store location before adding items',
        'error',
      );
      return;
    }
    this.itemSearch.set('');
    this.selectedItemId.set(null);
    this.showItemModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeItemModal(): void {
    this.showItemModal.set(false);
    document.body.style.overflow = 'auto';
  }

  selectItem(item: PickerItemView): void {
    if (!item.canIssue) {
      this.showToastMessage(
        item.unavailableReason ||
          'This item is not available to issue from the selected location',
        'error',
      );
      return;
    }
    this.selectedItemId.set(item.id);
  }

  addSelectedItem(): void {
    const itemId = this.selectedItemId();
    if (!itemId) {
      return;
    }

    const item = this.inventory.allItems().find((row) => row.id === itemId);
    if (!item) {
      return;
    }

    const picker = this.toPickerItem(item, this.branchId(), this.locationId());
    if (!picker.canIssue) {
      this.showToastMessage(
        picker.unavailableReason || 'This item is not available to issue',
        'error',
      );
      return;
    }

    const existing = this.lineItems().find((row) => row.itemId === item.id);
    if (existing) {
      if (this.applyLineQuantity(item.id, existing.quantity + 1)) {
        this.closeItemModal();
        this.showToastMessage(`${item.name} quantity increased`, 'success');
      }
      return;
    }

    this.lineItems.update((list) => [
      ...list,
      {
        itemId: item.id,
        code: item.code,
        name: item.name,
        category: item.category,
        available: picker.available,
        quantity: 1,
        reserved: false,
        price: picker.price || 0,
        unavailableReason: picker.unavailableReason,
        icon: item.icon,
      },
    ]);

    this.closeItemModal();
    this.showToastMessage(
      `${item.name} added from ${this.selectedLocationName()}`,
      'success',
    );
  }

  removeLineItem(itemId: string): void {
    const item = this.lineItems().find((row) => row.itemId === itemId);
    this.lineItems.update((list) =>
      list.filter((row) => row.itemId !== itemId),
    );
    if (item) {
      this.showToastMessage(`${item.name} removed`, 'info');
    }
  }

  updateLineQuantity(itemId: string, raw: unknown): void {
    const parsed = parsePositiveInt(raw, 'Quantity');
    if (!parsed.ok) {
      this.showToastMessage(
        parsed.message ?? 'Enter a valid quantity',
        'error',
      );
      return;
    }
    this.applyLineQuantity(itemId, parsed.value);
  }

  private applyLineQuantity(itemId: string, quantity: number): boolean {
    const line = this.lineItems().find((row) => row.itemId === itemId);
    if (!line) {
      return false;
    }
    const available = this.liveAvailableFor(itemId);
    const check = assertCanTake(available, quantity, line.name);
    if (!check.ok) {
      this.showToastMessage(
        check.message ?? 'Quantity exceeds remaining available',
        'error',
      );
      this.lineItems.update((list) =>
        list.map((row) =>
          row.itemId === itemId ? { ...row, available } : row,
        ),
      );
      return false;
    }
    this.lineItems.update((list) =>
      list.map((row) =>
        row.itemId === itemId ? { ...row, quantity, available } : row,
      ),
    );
    return true;
  }

  updateLinePrice(itemId: string, raw: unknown): void {
    const parsed = parseNonNegativeNumber(raw, 'Price');
    if (!parsed.ok) {
      this.showToastMessage(parsed.message ?? 'Enter a valid price', 'error');
      return;
    }
    this.lineItems.update((list) =>
      list.map((row) =>
        row.itemId === itemId ? { ...row, price: parsed.value } : row,
      ),
    );
  }

  private liveAvailableFor(itemId: string): number {
    return this.inventory.availableQuantityAtLocation(
      itemId,
      this.branchId(),
      this.locationId(),
      this.selectedLocationName(),
    );
  }

  private syncLineAvailability(): void {
    this.lineItems.update((list) =>
      list.map((row) => ({
        ...row,
        available: this.liveAvailableFor(row.itemId),
      })),
    );
  }

  private firstTakeViolation(): { ok: boolean; message?: string } {
    for (const line of this.lineItems()) {
      const parsed = parsePositiveInt(line.quantity, line.name);
      if (!parsed.ok) {
        return { ok: false, message: parsed.message };
      }
      const check = assertCanTake(
        this.liveAvailableFor(line.itemId),
        parsed.value,
        line.name,
      );
      if (!check.ok) {
        return check;
      }
    }
    return { ok: true };
  }

  toggleReserved(itemId: string, reserved: boolean): void {
    this.lineItems.update((list) =>
      list.map((row) => (row.itemId === itemId ? { ...row, reserved } : row)),
    );
  }

  openConfirmModal(): void {
    if (this.submitting()) {
      return;
    }
    if (!this.isFormValid()) {
      if (!this.issuedToCase().trim()) {
        this.showToastMessage('Enter the recipient name', 'error');
      } else if (!this.recipientPhone().trim()) {
        this.showToastMessage('Enter the recipient cell number', 'error');
      } else if (!this.recipientLocation().trim()) {
        this.showToastMessage('Enter the recipient location', 'error');
      } else if (!this.branchId().trim() || !this.locationId().trim()) {
        this.showToastMessage(
          'Select the branch and store location to take stock from',
          'error',
        );
      } else if (this.lineItems().length === 0) {
        this.showToastMessage('Please add at least one item', 'error');
      } else {
        this.showToastMessage('Please complete the required fields', 'error');
      }
      return;
    }

    this.inventory.loadItemMasterAndStock().subscribe(() => {
      this.syncLineAvailability();
      const violation = this.firstTakeViolation();
      if (!violation.ok) {
        this.showToastMessage(
          violation.message ??
            `An item exceeds remaining available at ${this.selectedLocationName()}`,
          'error',
        );
        return;
      }

      this.showConfirmModal.set(true);
      document.body.style.overflow = 'hidden';
    });
  }

  closeConfirmModal(): void {
    this.showConfirmModal.set(false);
    document.body.style.overflow = 'auto';
  }

  confirmIssue(): void {
    if (this.submitting()) {
      return;
    }
    this.submitIssue('issued');
  }

  saveDraft(): void {
    this.showToastMessage(
      'Save as pending is disabled. Issue or reserve from Current Stock.',
      'info',
    );
  }

  private submitIssue(status: 'issued' | 'pending'): void {
    const issuedTo = this.issuedToCase().trim();
    const phone = this.recipientPhone().trim();
    const dest = this.recipientLocation().trim();
    const branchId = this.branchId();
    const locationId = this.locationId();
    if (
      !issuedTo ||
      !phone ||
      !dest ||
      this.lineItems().length === 0 ||
      !branchId ||
      !locationId
    ) {
      this.openConfirmModal();
      return;
    }

    if (this.submitting()) {
      return;
    }
    this.submitting.set(true);

    const branchName = this.selectedBranchName() || branchId;
    const locationName = this.selectedLocationName();

    this.inventory
      .loadItemMasterAndStock()
      .pipe(
        switchMap(() => {
          this.syncLineAvailability();
          const violation = this.firstTakeViolation();
          if (!violation.ok) {
            return of({
              success: false as const,
              message: violation.message,
              hadReserved: false,
              hadConsumable: false,
            });
          }

          const lines = this.lineItems().map((line) => ({
            inventoryItemID: Number(line.itemId) || 0,
            quantity: line.quantity,
            unitCost: Number(line.price) || 0,
            itemName: line.name,
            isReserved: line.reserved,
          }));

          return this.inventory
            .createIssueStockViaApi({
              branchId,
              branchName,
              storeLocationId: locationId,
              locationName,
              issuedTo,
              recipientPhone: phone,
              recipientLocation: dest,
              issueDate: this.issueDate(),
              notes: this.notes(),
              status,
              lines,
            })
            .pipe(
              map((issueResult) => ({
                ...issueResult,
                hadReserved: lines.some((line) => line.isReserved),
                hadConsumable: lines.some((line) => !line.isReserved),
              })),
            );
        }),
        finalize(() => this.submitting.set(false)),
      )
      .subscribe((result) => {
        if (result.success) {
          this.finishIssueSuccess(issuedTo, {
            hadReserved: result.hadReserved,
            hadConsumable: result.hadConsumable,
          });
          this.inventory.refreshFromApi();
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to issue stock',
            'error',
          );
        }
      });
  }

  private finishIssueSuccess(
    issuedTo: string,
    split: { hadReserved: boolean; hadConsumable: boolean },
  ): void {
    this.lastIssuedTo.set(issuedTo);
    this.closeConfirmModal();
    this.lineItems.set([]);
    this.issuedToCase.set('');
    this.recipientPhone.set('');
    this.recipientLocation.set('');
    this.notes.set('');
    const parts: string[] = [];
    if (split.hadConsumable) {
      parts.push('issued items are on Issued Stock');
    }
    if (split.hadReserved) {
      parts.push(
        'reserved lines are on Issued Stock as Reserved — use Issue now to complete',
      );
    }
    const detail = parts.length ? ` ${parts.join('. ')}.` : '';
    this.showToastMessage(
      `Stock submitted for ${issuedTo}.${detail}`,
      'success',
    );
  }

  openEditIssueModal(issue: StockIssue): void {
    this.editingIssueId.set(issue.id);
    const branchId =
      this.branches().find(
        (row) => row.id === issue.branch || row.name === issue.branch,
      )?.id ?? '';

    this.editIssue.set({
      issuedTo: issue.issuedTo,
      branch: branchId,
      issueDate: issue.issueDate,
      items: issue.items,
      status: issue.status,
    });
    this.issuedBy.set(this.auth.currentUserName());
    this.showEditIssueModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeEditIssueModal(): void {
    this.showEditIssueModal.set(false);
    this.editingIssueId.set(null);
    document.body.style.overflow = 'auto';
  }

  updateEditIssue(
    field: keyof ReturnType<typeof this.editIssue>,
    value: string | number,
  ): void {
    if (field === 'items') {
      const parsed = parsePositiveInt(value, 'Items count');
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Enter a valid item count',
          'error',
        );
        return;
      }
      value = parsed.value;
    }
    this.editIssue.update((row) => ({ ...row, [field]: value }));
  }

  submitEditIssue(): void {
    if (this.saving()) {
      return;
    }

    const issue = this.editIssue();
    const editingId = this.editingIssueId();
    if (!editingId || !issue.issuedTo || !issue.branch || !issue.issueDate) {
      this.showToastMessage('Please fill in all required fields', 'error');
      return;
    }

    const branchName =
      this.branches().find((row) => row.id === issue.branch)?.name ??
      issue.branch;

    this.saving.set(true);
    this.inventory
      .updateIssueStockViaApi(editingId, {
        issuedTo: issue.issuedTo,
        issueDate: issue.issueDate,
        status: issue.status,
      })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe((result) => {
        if (result.success) {
          this.inventory.updateStockIssue(editingId, {
            issuedTo: issue.issuedTo,
            branch: branchName,
            issueDate: issue.issueDate,
            items: issue.items,
            status: issue.status,
          });
          this.closeEditIssueModal();
          this.showToastMessage('Issue updated successfully', 'success');
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to update issue',
            'error',
          );
        }
      });
  }

  resetForm(): void {
    if (this.lineItems().length > 0 || this.issuedToCase()) {
      if (!confirm('Discard all changes?')) {
        return;
      }
    }
    this.lineItems.set([]);
    this.issuedToCase.set('');
    this.recipientPhone.set('');
    this.recipientLocation.set('');
    this.issueDate.set(new Date().toISOString().slice(0, 10));
    this.issuedBy.set(this.auth.currentUserName());
    this.locationId.set('');
    this.notes.set('');
    this.showToastMessage('Form reset', 'info');
  }

  private showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  exportData(): void {
    const rows = this.issueStockList().map((issue) => ({
      'Issue No.': issue.issueNumber,
      'Issued To': issue.issuedTo,
      Phone: issue.recipientPhone ?? '',
      Destination: issue.recipientLocation ?? '',
      Item: issue.itemName ?? '',
      Branch: issue.branch,
      Location: issue.location ?? '',
      Qty: issue.items,
      'Total Value': issue.totalValue ?? 0,
      Date: issue.issueDate,
      Status: this.issueStatusLabel(issue.status),
    }));

    if (!rows.length) {
      this.showToastMessage('No issues or reserves to export yet.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `issued-stock-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }

  issueStatusLabel(status: StockIssue['status']): string {
    const map: Record<StockIssue['status'], string> = {
      pending: 'Pending',
      reserved: 'Reserved',
      issued: 'Issued',
      cancelled: 'Cancelled',
    };
    return map[status];
  }

  issueReservedHold(issue: StockIssue): void {
    if (
      this.submitting() ||
      (issue.status !== 'reserved' && !issue.isReservedHold)
    ) {
      return;
    }
    this.submitting.set(true);
    this.inventory
      .updateIssueStockViaApi(issue.id, {
        issuedTo: issue.issuedTo,
        issueDate: issue.issueDate,
        status: 'issued',
      })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            `Reserved stock ${issue.issueNumber} issued to ${issue.issuedTo}.`,
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to issue reserved stock',
            'error',
          );
        }
      });
  }

  releaseReservedHold(issue: StockIssue): void {
    if (
      this.submitting() ||
      (issue.status !== 'reserved' && !issue.isReservedHold)
    ) {
      return;
    }
    if (
      !confirm(
        `Release reserved hold ${issue.issueNumber}? Stock becomes available again.`,
      )
    ) {
      return;
    }
    this.submitting.set(true);
    this.inventory
      .updateIssueStockViaApi(issue.id, {
        issuedTo: issue.issuedTo,
        issueDate: issue.issueDate,
        status: 'cancelled',
      })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            `Reserved hold ${issue.issueNumber} released.`,
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to release reserved hold',
            'error',
          );
        }
      });
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if (
      (event.target as HTMLElement).classList.contains('modal-backdrop') ||
      (event.target as HTMLElement).classList.contains('modal-overlay') ||
      (event.target as HTMLElement).classList.contains('modal')
    ) {
      this.closeItemModal();
      this.closeConfirmModal();
      this.closeEditIssueModal();
    }
  }
}
